import React, { useState, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../../custom/customDropdown';
import { AuthContext } from '../../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const ObracunPlateZaglavljeTab = ({ valuteDB, stavkeDB, nacinDB, tipDB, onRefresh, toast }) => {
  const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);

    const emptyForm = {
        Naziv: '', TipTransakcije: null, NacinRacunanja: null,
        Iznos: 0, IznosBase: null, GodinePrag: null, valuta_id: 186, Redosled: 0,
    };

    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const invalid = (val) => formSubmitted && (val === null || val === undefined || val === '');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'NacinRacunanja') { next.IznosBase = null; next.GodinePrag = null; }
            return next;
        });
    };

    const resetForm = () => { setFormData(emptyForm); setSelectedId(null); setFormSubmitted(false); };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    Naziv: row.Naziv || '',
                    TipTransakcije: row.TipTransakcije ?? (tipDB?.[0]?.value ?? null),
                    NacinRacunanja: row.NacinRacunanja ?? (nacinDB?.[0]?.value ?? null),
                    Iznos: row.Iznos ?? 0,
                    IznosBase: row.IznosBase ?? null,
                    GodinePrag: row.GodinePrag ?? null,
                    valuta_id: row.valuta_id ?? 186,
                    Redosled: row.Redosled ?? 0,
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!formData.Naziv?.trim()) {
            toast.current.show({ severity: 'warn', summary: t('admin.m_popunite_naziv'), life: 3000 });
            return;
        }
        fetch(process.env.REACT_APP_BACKEND_URL + '/finance/ManageObracunPlateZaglavlje', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_STAVKA', id: selectedId || 0, ...formData }),
        })
            .then(r => r.json())
            .then(data => {
                const row = Array.isArray(data) ? data[0] : data;
                if (row?.ID) {
                    toast.current.show({ severity: 'success', summary: row.message || 'Uspešno!', life: 3000 });
                    resetForm();
                    onRefresh();
                } else {
                    toast.current.show({ severity: 'warn', summary: row?.message || 'Greška', life: 3000 });
                }
            })
            .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_snimanju'), life: 3000 }));
    };

    const handleDelete = () => {
        if (!selectedId) { toast.current.show({ severity: 'warn', summary: t('admin.m_izaberite_stavku_za_brisanje'), life: 3000 }); return; }
        confirmDialog({
            message: 'Obrisati izabranu stavku zaglavlja?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/finance/ManageObracunPlateZaglavlje', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE_STAVKA', id: selectedId }),
                })
                    .then(r => r.json())
                    .then(data => {
                        const row = Array.isArray(data) ? data[0] : data;
                        toast.current.show({ severity: 'success', summary: row?.message || 'Obrisano!', life: 3000 });
                        resetForm();
                        onRefresh();
                    })
                    .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_brisanju'), life: 3000 }));
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet('ZaglavljeStavke');
        const headers = ['Naziv', 'Tip', 'Način računanja', 'Iznos/Stopa', 'Iznos base', 'God. prag', 'Valuta', 'Redosled'];
        const hr = ws.addRow(headers);
        hr.font = { bold: true };
        hr.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (stavkeDB || []).forEach(r => {
            const row = ws.addRow([
                r.Naziv,
                (tipDB || []).find(t => t.value === r.TipTransakcije)?.label || r.TipTransakcije,
                (nacinDB || []).find(n => n.value === r.NacinRacunanja)?.label || r.NacinRacunanja,
                r.Iznos, r.IznosBase || '', r.GodinePrag || '',
                r.ValutaNaziv || '', r.Redosled,
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        ws.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, String(cell.value).length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Zaglavlje stavke - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    /* nacinDB je sortirano po id (redosled INSERT u modgen grupi 46):
       0=Rucno, 1=Fiksni iznos, 2=Po danu rada, 3=Po godinama rada, 4=Gorivo za licne potrebe */
    const idDanRada     = nacinDB?.[2]?.value ?? null;
    const idGodineRada  = nacinDB?.[3]?.value ?? null;
    const idGorivoLicno = nacinDB?.[4]?.value ?? null;
    const showGodine    = idGodineRada  !== null && formData.NacinRacunanja === idGodineRada;
    const isGorivoLicno = idGorivoLicno !== null && formData.NacinRacunanja === idGorivoLicno;

    const labelIznosMap = {};
    if (nacinDB) {
        const displayLabels = ['Podrazumevani iznos', 'Fiksni iznos', 'Iznos po danu', 'Prirast po godini', 'Iznos (automatski iz točenja)'];
        nacinDB.forEach((n, i) => { labelIznosMap[n.value] = displayLabels[i] || 'Iznos'; });
    }
    const labelIznos = labelIznosMap[formData.NacinRacunanja] || 'Iznos';

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div style={{ marginTop: '1%' }}>
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off">
                        <div className="gridLicni">
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.naziv')}:</label>
                                <InputText name="Naziv" value={formData.Naziv}
                                    onChange={handleChange}
                                    invalid={invalid(formData.Naziv)} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>Tip transakcije:</label>
                                <CustomDropdown name="TipTransakcije" value={formData.TipTransakcije}
                                    options={tipDB || []} optionLabel="label" placeholder={t('nova.tip')}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.nacinRacunanja')}:</label>
                                <CustomDropdown name="NacinRacunanja" value={formData.NacinRacunanja}
                                    options={nacinDB || []} optionLabel="label" placeholder={t('admin.nacin')}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.valuta')}:</label>
                                <CustomDropdown name="valuta_id" value={formData.valuta_id}
                                    options={valuteDB || []} optionLabel="label" placeholder={t('nova.valuta')}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{labelIznos}:</label>
                                <InputNumber name="Iznos" value={formData.Iznos}
                                    onValueChange={handleChange}
                                    disabled={isGorivoLicno}
                                    minFractionDigits={0} maxFractionDigits={2} />
                                {isGorivoLicno && <small style={{ color: '#6c757d', fontSize: 11, marginTop: 2 }}>Iznos se računa automatski iz točenja i cena goriva pri generisanju obračuna.</small>}
                            </div>
                            {showGodine && (
                                <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label>{t('admin.iznosBaseGod')}:</label>
                                    <InputNumber name="IznosBase" value={formData.IznosBase}
                                        onValueChange={handleChange}
                                        minFractionDigits={2} maxFractionDigits={2} />
                                </div>
                            )}
                            {showGodine && (
                                <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label>{t('admin.pragGod')}:</label>
                                    <InputNumber name="GodinePrag" value={formData.GodinePrag}
                                        onValueChange={handleChange}
                                        minFractionDigits={0} maxFractionDigits={0} />
                                </div>
                            )}
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.redosledPrikaza')}:</label>
                                <InputNumber name="Redosled" value={formData.Redosled}
                                    onValueChange={handleChange}
                                    minFractionDigits={0} maxFractionDigits={0} />
                            </div>
                            {showGodine && (
                                <div className="col-12" style={{ color: '#6c757d', fontSize: 12, paddingTop: 4 }}>
                                    Formula: ako vozač ima &lt; <b>{formData.GodinePrag ?? 2}</b> god. → 0 &nbsp;|&nbsp;
                                    inače: <b>{formData.IznosBase ?? 0}</b> + (godine − {formData.GodinePrag ?? 2}) × <b>{formData.Iznos ?? 0}</b>
                                </div>
                            )}
                            {idDanRada !== null && formData.NacinRacunanja === idDanRada && (
                                <div className="col-12" style={{ color: '#6c757d', fontSize: 12, paddingTop: 4 }}>
                                    Formula: broj radnih dana × <b>{formData.Iznos ?? 0}</b>
                                </div>
                            )}
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable className="stavke-tabela" value={stavkeDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                        header={tableHeader} emptyMessage="Nema konfigurisanih stavki zaglavlja"
                        resizableColumns globalFilter={globalFilter}
                        rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="Naziv" header={t('admin.naziv')} sortable style={{ minWidth: '160px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column header={t('nova.tip')} sortable style={{ minWidth: '90px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={r => (tipDB || []).find(t => t.value === r.TipTransakcije)?.label || r.TipTransakcije} />
                        <Column header={t('admin.nacinRacunanja')} style={{ minWidth: '160px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={r => (nacinDB || []).find(n => n.value === r.NacinRacunanja)?.label || r.NacinRacunanja} />
                        <Column field="Iznos" header={t('admin.iznosStopa')} style={{ minWidth: '100px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={r => r.Iznos != null ? parseFloat(Number(r.Iznos).toFixed(2)).toString() : '-'} />
                        <Column field="IznosBase" header={t('admin.iznosBase')} style={{ minWidth: '100px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={r => r.IznosBase != null ? parseFloat(Number(r.IznosBase).toFixed(2)).toString() : '-'} />
                        <Column field="GodinePrag" header={t('admin.godPrag')} style={{ minWidth: '80px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }}
                            body={r => r.GodinePrag ?? '-'} />
                        <Column field="ValutaNaziv" header={t('nova.valuta')} style={{ minWidth: '80px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                        <Column field="Redosled" header={t('admin.redosled')} sortable style={{ minWidth: '80px', textAlign: 'center' }} headerStyle={{ textAlign: 'center' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default ObracunPlateZaglavljeTab;
